import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../utils/api';
import type { AwCtx } from '../../ctx';
import { baht, errBox, note, pillAmber } from '../../ui';
import { Dialog, Foot, cancelBtn, field, isoDate, lbl, todayIso, useDialogClose } from '../../dialog';
import { FaAsset, toSatang } from './shared';

// ── The asset window's small pieces (batch 9 R4 — COA-H3 / H6) ───────────────────────────────────
// The three ACTIONS an asset takes that are not an edit — dispose of it, write it off, delete it — are
// small dialogs over the full-screen window (ANA:368: documents full screen, action dialogs small).
// Each one asks for what its route needs and nothing else, and shows the server's refusal as the
// server words it: whether a date is inside the open period, whether an account may be posted to,
// whether anything was charged — those are the server's rules (services/fixedAssets.ts), never
// re-guessed here. Dispose and write-off create a DRAFT entry; the window says so with its number.
// Also here, because the window and the form both draw them: the account label, the "set by hand"
// marker and the read-only fact.

/** A postable account of the client, as GET /journal/accounts sends it. */
export type FaAcct = { id: string; code: string; name_th: string; name_en: string; class: string };
type TF = (key: string, opts?: Record<string, unknown>) => string;

/** The name to show in the reader's language: Thai for Thai, otherwise English when there is one. */
export const pickName = (lang: string, th: string | null | undefined, en: string | null | undefined): string =>
  (String(lang || '').startsWith('th') ? (th || en) : (en || th)) || '';
/** "code · name" — how every account reads on this screen. */
export const acctLabel = (lang: string, a: { code: string; name_th?: string; name_en?: string; nameTh?: string; nameEn?: string } | null | undefined): string =>
  a ? `${a.code} · ${pickName(lang, a.name_th ?? a.nameTh, a.name_en ?? a.nameEn)}` : '';
/** A rate or a life, without trailing zeros: 20 → "20", 33.333333 → "33.33". */
export const fmtNum = (n: any, digits = 2): string => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  return String(Math.round(v * 10 ** digits) / 10 ** digits);
};

/** COA-H3: a setting this asset holds against its category. Shown wherever the setting is shown. */
export const SetByHand = ({ t }: { t: TF }) => (
  <span data-hand style={{ ...pillAmber, marginLeft: 6, textTransform: 'none', letterSpacing: 0 }} title={t('aw_faa_hand_title')}>
    <i className="ti ti-hand-finger" aria-hidden /> {t('aw_faa_hand')}
  </span>
);

/** One read-only fact: a small uppercase label over its value. `k` names it for the tests. */
export const Fact = ({ k, label, children, hand, t, wide }: { k: string; label: string; children: React.ReactNode; hand?: boolean; t: TF; wide?: boolean }) => (
  <div data-field={k} style={{ minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
      {label}{hand && <SetByHand t={t} />}
    </div>
    <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 2, overflowWrap: 'anywhere' }}>{children}</div>
  </div>
);

/** A Close for a footer that has no Save — through the window's own gate (dialog.tsx useDialogClose). */
export const CloseBtn = ({ onClose, t }: { onClose: () => void; t: TF }) => {
  const close = useDialogClose(onClose);
  return <button type="button" onClick={close} style={cancelBtn}>{t('aw_dlg_close')}</button>;
};

/** The client's postable accounts, read once per dialog. */
function useAccounts(clientId: string): { accts: FaAcct[] | null; err: string } {
  const [accts, setAccts] = useState<FaAcct[] | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.jnlAccounts(clientId).then((d: any) => { if (alive) setAccts(d?.accounts || []); })
      .catch((e: any) => { if (alive) { setAccts([]); setErr(String(e?.message || e)); } });
    return () => { alive = false; };
  }, [clientId]);
  return { accts, err };
}

/** The day an action defaults to: today when it falls in the period on screen, else the period's end. */
const defaultDay = (ctx: AwCtx): string => {
  const d = todayIso();
  const p = ctx.period;
  if (!p) return d;
  if (d < p.start_date) return p.start_date;
  if (d > p.end_date) return p.end_date;
  return d;
};

const AcctSelect = ({ value, onChange, accts, lang, t, k }: { value: string; onChange: (v: string) => void; accts: FaAcct[]; lang: string; t: TF; k: string }) => (
  <select data-f={k} value={value} onChange={e => onChange(e.target.value)} style={field}>
    <option value="">{t('aw_faa_choose')}</option>
    {accts.map(a => <option key={a.id} value={a.id}>{acctLabel(lang, a)}</option>)}
  </select>
);

/** The asset the dialog acts on, as one line at its top. */
const Subject = ({ a, lang, t }: { a: FaAsset; lang: string; t: TF }) => (
  <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
    <b style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{a.asset_no}</b> · {pickName(lang, a.name_th, a.name_en)}
    <div style={note}>{t('aw_faa_act_nbv', { amount: baht(a.nbv) })}</div>
  </div>
);

export type FaPosted = { entryId: string; docNo: string; gainLoss?: number; loss?: number };

/** COA-H6: sell (or otherwise dispose of) the asset. Proceeds are typed in baht and SENT IN SATANG. */
export function DisposeDialog({ ctx, asset, onClose, onDone }: { ctx: AwCtx; asset: FaAsset; onClose: () => void; onDone: (r: FaPosted) => void }) {
  const { t, i18n } = useTranslation();
  const { accts, err: acctErr } = useAccounts(ctx.clientId);
  const [date, setDate] = useState(() => defaultDay(ctx));
  const [proceeds, setProceeds] = useState('');
  const [proceedsAccountId, setProceedsAccountId] = useState('');
  const [gainLossAccountId, setGainLossAccountId] = useState('');
  const [memo, setMemo] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const amount = toSatang(proceeds);
  // ⛔ The proceeds account is asked for only when something was received — the server needs it then
  // and ignores it otherwise (services/fixedAssets.ts disposeAsset).
  const ready = isoDate(date) && amount >= 0 && !!gainLossAccountId && (amount === 0 || !!proceedsAccountId);
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      const r: FaPosted = await api.faDispose(asset.id, { date, proceeds: amount, proceedsAccountId: amount > 0 ? proceedsAccountId : '', gainLossAccountId, note: memo.trim() });
      onDone(r);
    } catch (e: any) { setErr(String(e?.message || e)); setBusy(false); }
  };
  const lang = i18n.language;
  const assetAccts = (accts || []).filter(a => a.class === 'asset');
  const plAccts = (accts || []).filter(a => a.class === 'income' || a.class === 'expense');
  return (
    <Dialog title={t('aw_faa_dispose_title')} onClose={onClose} busy={busy} dirty={dirty} width={500}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-arrow-bar-right" label={t('aw_faa_dispose_btn')} t={t} />}>
      <div data-fa-dialog="dispose" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Subject a={asset} lang={lang} t={t} />
        <div>
          <label style={lbl}>{t('aw_faa_act_date')}</label>
          <input data-f="date" type="date" value={date} onChange={e => ed(setDate, e.target.value)} style={field} />
        </div>
        <div>
          <label style={lbl}>{t('aw_faa_dispose_proceeds')}</label>
          <input data-f="proceeds" type="number" min="0" step="0.01" value={proceeds} onChange={e => ed(setProceeds, e.target.value)} style={field} placeholder="0.00" />
        </div>
        {amount > 0 && (
          <div>
            <label style={lbl}>{t('aw_faa_dispose_proceeds_acct')}</label>
            <AcctSelect k="proceedsAccount" value={proceedsAccountId} onChange={v => ed(setProceedsAccountId, v)} accts={assetAccts} lang={lang} t={t} />
          </div>
        )}
        <div>
          <label style={lbl}>{t('aw_faa_dispose_gl_acct')}</label>
          <AcctSelect k="gainLossAccount" value={gainLossAccountId} onChange={v => ed(setGainLossAccountId, v)} accts={plAccts} lang={lang} t={t} />
        </div>
        <div>
          <label style={lbl}>{t('aw_faa_act_note')}</label>
          <textarea data-f="note" value={memo} onChange={e => ed(setMemo, e.target.value)} rows={2} maxLength={500} style={{ ...field, resize: 'vertical' }} />
        </div>
        <div style={note}>{t('aw_faa_dispose_hint')}</div>
        {(err || acctErr) && <div style={errBox}>{err || acctErr}</div>}
      </div>
    </Dialog>
  );
}

/** COA-H6: the asset is gone and nothing was received — a disposal for nothing, posted as a loss. */
export function WriteOffDialog({ ctx, asset, onClose, onDone }: { ctx: AwCtx; asset: FaAsset; onClose: () => void; onDone: (r: FaPosted) => void }) {
  const { t, i18n } = useTranslation();
  const { accts, err: acctErr } = useAccounts(ctx.clientId);
  const [date, setDate] = useState(() => defaultDay(ctx));
  const [lossAccountId, setLossAccountId] = useState('');
  const [memo, setMemo] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const ready = isoDate(date) && !!lossAccountId;
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try { onDone(await api.faWriteOff(ctx.clientId, asset.id, { date, lossAccountId, note: memo.trim() })); }
    catch (e: any) { setErr(String(e?.message || e)); setBusy(false); }
  };
  const lang = i18n.language;
  return (
    <Dialog title={t('aw_faa_writeoff_title')} onClose={onClose} busy={busy} dirty={dirty} width={500}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-trash-x" label={t('aw_faa_writeoff_btn')} t={t} />}>
      <div data-fa-dialog="writeoff" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Subject a={asset} lang={lang} t={t} />
        <div>
          <label style={lbl}>{t('aw_faa_act_date')}</label>
          <input data-f="date" type="date" value={date} onChange={e => ed(setDate, e.target.value)} style={field} />
        </div>
        <div>
          <label style={lbl}>{t('aw_faa_writeoff_acct')}</label>
          <AcctSelect k="lossAccount" value={lossAccountId} onChange={v => ed(setLossAccountId, v)} accts={(accts || []).filter(a => a.class === 'expense')} lang={lang} t={t} />
        </div>
        <div>
          <label style={lbl}>{t('aw_faa_act_note')}</label>
          <textarea data-f="note" value={memo} onChange={e => ed(setMemo, e.target.value)} rows={2} maxLength={500} style={{ ...field, resize: 'vertical' }} />
        </div>
        <div style={note}>{t('aw_faa_writeoff_hint')}</div>
        {(err || acctErr) && <div style={errBox}>{err || acctErr}</div>}
      </div>
    </Dialog>
  );
}

/** An asset registered by mistake: soft, with a reason. The window offers it only when nothing has
 *  been charged and it was never disposed of; the server says the same and has the last word. */
export function DeleteDialog({ ctx, asset, onClose, onDone }: { ctx: AwCtx; asset: FaAsset; onClose: () => void; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ready = !!reason.trim();
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try { await api.faDelete(ctx.clientId, asset.id, reason.trim()); onDone(); }
    catch (e: any) { setErr(String(e?.message || e)); setBusy(false); }
  };
  return (
    <Dialog title={t('aw_faa_delete_title')} onClose={onClose} busy={busy} dirty={dirty} width={460}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-trash" label={t('aw_faa_delete_btn')} t={t} />}>
      <div data-fa-dialog="delete" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Subject a={asset} lang={i18n.language} t={t} />
        <div>
          <label style={lbl}>{t('aw_faa_delete_reason')}</label>
          <textarea data-f="reason" value={reason} onChange={e => { setDirty(true); setReason(e.target.value); }} rows={3} maxLength={300} style={{ ...field, resize: 'vertical' }} />
          <div style={note}>{t('aw_faa_delete_hint')}</div>
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}
